import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import {
  DrugLicenseStatus,
  drugLicenseService,
} from "../../services/drug-license.service";
import { showToast } from "../../lib/toast";
import { useAuthStore } from "../../stores/authStore";

const LICENSE_TYPES = [
  "RETAIL",
  "WHOLESALE",
  "RETAIL_WHOLESALE",
  "MANUFACTURING",
  "IMPORT",
  "EXPORT",
];

const SCHEDULES = [
  "SCHEDULE_G",
  "SCHEDULE_H",
  "SCHEDULE_H1",
  "SCHEDULE_X",
  "NDPS",
  "CONTROLLED",
];

const LICENSE_STATUSES: DrugLicenseStatus[] = [
  "ACTIVE",
  "PENDING_RENEWAL",
  "SUSPENDED",
  "CANCELLED",
  "EXPIRED",
];

interface UpdateDrugLicenseData {
  storeId: string;
  licenseNumber: string;
  licenseType: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
  registeredPharmacist: string;
  pharmacistRegNumber: string;
  documentUrl: string;
  notes: string;
  schedulesCovered: string[];
  status: DrugLicenseStatus;
}

export function EditDrugLicensePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalDocuments, setOriginalDocuments] = useState<string[]>([]);
  const [formData, setFormData] = useState<UpdateDrugLicenseData>({
    storeId: "",
    licenseNumber: "",
    licenseType: "RETAIL",
    issuingAuthority: "",
    issueDate: "",
    expiryDate: "",
    registeredPharmacist: "",
    pharmacistRegNumber: "",
    documentUrl: "",
    notes: "",
    schedulesCovered: [],
    status: "ACTIVE",
  });

  useEffect(() => {
    if (id) {
      loadLicense();
    }
  }, [id]);

  const loadLicense = async () => {
    try {
      setLoading(true);
      const data = await drugLicenseService.getById(id!);
      setOriginalDocuments(data.documents ?? []);
      setFormData({
        storeId: data.storeId,
        licenseNumber: data.licenseNumber,
        licenseType: data.licenseType,
        issuingAuthority: data.issuingAuthority,
        issueDate: data.issueDate.split("T")[0],
        expiryDate: data.expiryDate.split("T")[0],
        registeredPharmacist: data.registeredPharmacist || "",
        pharmacistRegNumber: data.pharmacistRegNumber || "",
        documentUrl: data.documents?.[0] || data.documentUrl || "",
        notes: data.notes || "",
        schedulesCovered: data.schedulesCovered ?? [],
        status: data.status,
      });
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load drug license",
      );
      navigate("/drug-licenses");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.storeId ||
      !formData.licenseNumber ||
      !formData.licenseType ||
      !formData.issuingAuthority ||
      !formData.issueDate ||
      !formData.expiryDate
    ) {
      showToast.error("Please fill in all required fields");
      return;
    }

    if (formData.storeId !== selectedStoreId) {
      showToast.error(
        "Switch the active location in the header before editing this licence",
      );
      return;
    }

    if (formData.expiryDate <= formData.issueDate) {
      showToast.error("Expiry date must be after the issue date");
      return;
    }

    try {
      setSaving(true);
      const { documentUrl, storeId: _storeId, ...licenseData } = formData;
      const firstDocument = documentUrl.trim();
      const documents = [
        ...(firstDocument ? [firstDocument] : []),
        ...originalDocuments.slice(1),
      ].filter((value, index, all) => all.indexOf(value) === index);
      await drugLicenseService.update(id!, { ...licenseData, documents });
      showToast.success("Drug license updated successfully");
      navigate("/drug-licenses");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to update drug license",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/drug-licenses")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Drug Licenses
        </button>

        <h1 className="text-2xl font-bold text-gray-900">Edit Drug License</h1>
        <p className="text-sm text-gray-500 mt-1">
          Update drug license information
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* License Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            License Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Store *
              </label>
              <select
                name="storeId"
                value={formData.storeId}
                disabled
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a location from the header</option>
                {formData.storeId && (
                  <option value={formData.storeId}>
                    {user?.stores?.find(
                      (store) => store.id === formData.storeId,
                    )?.name ?? "Licence store"}
                  </option>
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.storeId === selectedStoreId
                  ? "This licence belongs to the active location."
                  : "Switch to this location from the header before saving."}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Type *
              </label>
              <select
                name="licenseType"
                value={formData.licenseType}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {LICENSE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("_", " ")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Number *
              </label>
              <input
                type="text"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issuing Authority *
              </label>
              <input
                type="text"
                name="issuingAuthority"
                value={formData.issuingAuthority}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Issue Date *
              </label>
              <input
                type="date"
                name="issueDate"
                value={formData.issueDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date *
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Pharmacist Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Registered Pharmacist
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pharmacist Name
              </label>
              <input
                type="text"
                name="registeredPharmacist"
                value={formData.registeredPharmacist}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Number
              </label>
              <input
                type="text"
                name="pharmacistRegNumber"
                value={formData.pharmacistRegNumber}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Additional Information
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">
                Schedules Covered
              </legend>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SCHEDULES.map((schedule) => (
                  <label
                    key={schedule}
                    className="flex items-center gap-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={formData.schedulesCovered.includes(schedule)}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          schedulesCovered: event.target.checked
                            ? [...previous.schedulesCovered, schedule]
                            : previous.schedulesCovered.filter(
                                (value) => value !== schedule,
                              ),
                        }))
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {schedule.replace("_", " ")}
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document URL
              </label>
              <input
                type="url"
                name="documentUrl"
                value={formData.documentUrl}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {LICENSE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status.replace("_", " ")}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate("/drug-licenses")}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || formData.storeId !== selectedStoreId}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditDrugLicensePage;
